/**
 * Translation Service
 * Manages Google Translate integration with caching, viewport awareness, and debouncing
 */

class TranslationService {
    constructor() {
        this.currentLang = this.getSavedLanguage() || 'en';
        this.translationCache = this.loadCacheFromStorage();
        this.debounceTimer = null;
        this.debounceDelay = 500; // ms
        this.visibleComments = new Set();
        this.pendingComments = new Set();
    }

    /**
     * Get saved language preference from localStorage
     */
    getSavedLanguage() {
        try {
            const saved = localStorage.getItem('herndonmap_language');
            return saved || 'en';
        } catch (e) {
            console.warn('Could not access localStorage for language preference:', e);
            return 'en';
        }
    }

    /**
     * Save language preference to localStorage
     */
    saveLanguage(langCode) {
        try {
            localStorage.setItem('herndonmap_language', langCode);
            this.currentLang = langCode;
        } catch (e) {
            console.warn('Could not save language preference:', e);
            this.currentLang = langCode;
        }
    }

    /**
     * Load translation cache from localStorage
     */
    loadCacheFromStorage() {
        try {
            const cached = localStorage.getItem('herndonmap_translation_cache');
            return cached ? JSON.parse(cached) : {};
        } catch (e) {
            console.warn('Could not load translation cache:', e);
            return {};
        }
    }

    /**
     * Save translation cache to localStorage
     */
    saveCacheToStorage() {
        try {
            localStorage.setItem('herndonmap_translation_cache', JSON.stringify(this.translationCache));
        } catch (e) {
            console.warn('Could not save translation cache:', e);
        }
    }

    /**
     * Translate UI elements immediately
     * Focus on: page title, buttons, labels, sidebar content
     */
    translateUI(langCode) {
        console.log('=== Translating UI to', langCode, '===');
        this.currentLang = langCode;
        this.saveLanguage(langCode);

        if (langCode === 'en') {
            console.log('Language is English, no translation needed');
            return;
        }

        // Check cache first
        const cacheKey = `ui_${langCode}`;
        if (this.translationCache[cacheKey]) {
            console.log('Using cached UI translations');
            this.applyUITranslations(this.translationCache[cacheKey]);
            return;
        }

        // Get Google Translate combo and trigger
        const googleCombo = document.querySelector('.goog-te-combo');
        if (!googleCombo) {
            console.error('Google Translate combo not found');
            return;
        }

        console.log('Triggering Google Translate to', langCode);
        googleCombo.value = langCode;
        
        // Dispatch events to trigger translation
        setTimeout(() => {
            googleCombo.dispatchEvent(new Event('change', { bubbles: true }));
            googleCombo.dispatchEvent(new Event('blur'));
            
            // Cache the UI translations after a delay (let GT do its work)
            setTimeout(() => {
                this.cacheUITranslations(langCode);
            }, 1500);
        }, 100);
    }

    /**
     * Cache current UI translations
     */
    cacheUITranslations(langCode) {
        try {
            const uiText = {
                pageTitle: document.title,
                headerElements: Array.from(document.querySelectorAll('h1, h2, h3, h4, .sideComment h3'))
                    .map(el => ({ selector: el.className, text: el.textContent }))
                    .slice(0, 20), // Limit size
            };
            
            const cacheKey = `ui_${langCode}`;
            this.translationCache[cacheKey] = uiText;
            this.saveCacheToStorage();
            console.log('✓ Cached UI translations for', langCode);
        } catch (e) {
            console.warn('Could not cache UI translations:', e);
        }
    }

    /**
     * Apply cached UI translations (for instant switching)
     */
    applyUITranslations(uiData) {
        // This is a fallback if we have cached data
        // The real translation is done by Google Translate itself
        console.log('Applying cached UI translations');
    }

    /**
     * Mark a comment as visible in the viewport
     */
    markCommentVisible(commentId) {
        if (!this.visibleComments.has(commentId)) {
            this.visibleComments.add(commentId);
            
            // If not English and not already pending, add to pending translation queue
            if (this.currentLang !== 'en' && !this.pendingComments.has(commentId)) {
                this.pendingComments.add(commentId);
                this.debouncedTranslateComments();
            }
        }
    }

    /**
     * Mark a comment as invisible
     */
    markCommentInvisible(commentId) {
        this.visibleComments.delete(commentId);
    }

    /**
     * Debounced comment translation
     * Waits 500ms before translating to batch requests
     */
    debouncedTranslateComments() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.translateVisibleComments();
        }, this.debounceDelay);
    }

    /**
     * Translate only visible comments
     */
    translateVisibleComments() {
        const commentsToTranslate = Array.from(this.pendingComments);
        
        if (commentsToTranslate.length === 0) {
            console.log('No comments to translate');
            return;
        }

        console.log(`Translating ${commentsToTranslate.length} visible comments to ${this.currentLang}`);

        commentsToTranslate.forEach(commentId => {
            const commentEl = document.querySelector(`[data-comment-id="${commentId}"]`);
            if (commentEl) {
                // Check cache
                const cacheKey = `comment_${commentId}_${this.currentLang}`;
                if (this.translationCache[cacheKey]) {
                    // Apply cached translation
                    this.applyCommentTranslation(commentEl, this.translationCache[cacheKey]);
                    this.pendingComments.delete(commentId);
                } else {
                    // Mark for translation by Google Translate (it will handle the element)
                    // Store original text for caching
                    const originalText = commentEl.textContent;
                    this.markForGoogleTranslation(commentEl, commentId, originalText);
                }
            }
        });
    }

    /**
     * Mark comment element for Google Translate
     */
    markForGoogleTranslation(element, commentId, originalText) {
        // Add a class that triggers Google Translate's observer
        element.classList.add('translate-me');
        
        // After GT translates, cache the result
        setTimeout(() => {
            const translatedText = element.textContent;
            if (translatedText !== originalText) {
                const cacheKey = `comment_${commentId}_${this.currentLang}`;
                this.translationCache[cacheKey] = translatedText;
                this.saveCacheToStorage();
                console.log(`✓ Cached translation for comment ${commentId}`);
            }
            this.pendingComments.delete(commentId);
        }, 1000);
    }

    /**
     * Apply cached comment translation
     */
    applyCommentTranslation(element, translatedText) {
        element.textContent = translatedText;
    }

    /**
     * Get current language
     */
    getCurrentLanguage() {
        return this.currentLang;
    }

    /**
     * Initialize intersection observer for viewport detection
     * Should be called from React component
     */
    initializeViewportObserver() {
        if (typeof IntersectionObserver === 'undefined') {
            console.warn('IntersectionObserver not supported');
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const commentId = entry.target.getAttribute('data-comment-id');
                if (entry.isIntersecting) {
                    this.markCommentVisible(commentId);
                } else {
                    this.markCommentInvisible(commentId);
                }
            });
        }, {
            rootMargin: '100px', // Start loading 100px before comment enters viewport
        });

        return observer;
    }
}

// Export singleton instance
export const translationService = new TranslationService();
export default TranslationService;
